import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface DstProps {
  onNavigate: (to: string) => void;
}

const MODULE = "chartwork";
const GAME = "dst";
const CARDS_PER_SESSION = 12;
const CHOICES_PER_CARD = 4;

type Solve = "distance" | "speed" | "time";

interface Card {
  solve: Solve;
  speed: number; // kt
  timeMin: number; // minutes
  distance: number; // nm
  prompt: string;
  formula: string;
  correct: string;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function trim(n: number): string {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : String(r);
}

const fmtNm = (v: number) => `${trim(v)} nm`;
const fmtKt = (v: number) => `${trim(v)} kt`;
const fmtHours = (min: number) => `${trim(min / 60)} h`;

function fmtTime(min: number): string {
  if (min % 60 === 0) return `${min / 60} h`;
  if (min > 60) return `${Math.floor(min / 60)} h ${min % 60} min`;
  return `${min} min`;
}

/// Take the first `n` distinct, valid distractors from `candidates`,
/// excluding the correct value, then pad from a fallback stepper.
function distractors(
  correct: number,
  candidates: number[],
  fallbackStep: number,
  valid: (n: number) => boolean,
): number[] {
  const out: number[] = [];
  const seen = new Set<number>([correct]);
  for (const c of candidates) {
    if (out.length >= CHOICES_PER_CARD - 1) break;
    if (valid(c) && !seen.has(c)) {
      seen.add(c);
      out.push(c);
    }
  }
  let step = fallbackStep;
  while (out.length < CHOICES_PER_CARD - 1) {
    for (const c of [correct + step, correct - step]) {
      if (out.length >= CHOICES_PER_CARD - 1) break;
      if (valid(c) && !seen.has(c)) {
        seen.add(c);
        out.push(c);
      }
    }
    step += fallbackStep;
  }
  return out;
}

function shuffle(arr: string[]): string[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = ai;
  }
  return arr;
}

function buildCard(): Card {
  const speed = randInt(4, 12);
  // Half-hour multiples keep distance a tidy multiple of 0.5 nm.
  const timeMin = pick([30, 60, 90, 120]);
  const hours = timeMin / 60;
  const distance = Math.round(speed * hours * 10) / 10;
  const solve: Solve = pick(["distance", "speed", "time"]);

  if (solve === "distance") {
    const ds = distractors(
      distance,
      [distance * 1.5, distance / 2, distance + 1, distance - 1, distance * 2],
      0.5,
      (n) => n > 0,
    ).map((n) => Math.round(n * 10) / 10);
    return {
      solve,
      speed,
      timeMin,
      distance,
      prompt: `How far do you travel at ${fmtKt(speed)} for ${fmtTime(timeMin)}?`,
      formula: `${fmtKt(speed)} × ${fmtHours(timeMin)} = ${fmtNm(distance)}`,
      correct: fmtNm(distance),
      choices: shuffle([distance, ...ds].map(fmtNm)),
    };
  }
  if (solve === "speed") {
    const ds = distractors(
      speed,
      [speed + 1, speed - 1, speed + 2, speed - 2, speed * 2, Math.round(speed / 2)],
      1,
      (n) => n > 0 && Number.isInteger(n),
    );
    return {
      solve,
      speed,
      timeMin,
      distance,
      prompt: `What speed covers ${fmtNm(distance)} in ${fmtTime(timeMin)}?`,
      formula: `${fmtNm(distance)} ÷ ${fmtHours(timeMin)} = ${fmtKt(speed)}`,
      correct: fmtKt(speed),
      choices: shuffle([speed, ...ds].map(fmtKt)),
    };
  }
  // solve time
  const ds = distractors(
    timeMin,
    [timeMin + 30, timeMin - 30, timeMin * 2, timeMin / 2, timeMin + 15],
    15,
    (n) => n > 0 && n % 15 === 0,
  );
  return {
    solve,
    speed,
    timeMin,
    distance,
    prompt: `How long to cover ${fmtNm(distance)} at ${fmtKt(speed)}?`,
    formula: `${fmtNm(distance)} ÷ ${fmtKt(speed)} = ${fmtHours(timeMin)}`,
    correct: fmtTime(timeMin),
    choices: shuffle([timeMin, ...ds].map(fmtTime)),
  };
}

export function Dst({ onNavigate }: DstProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.correct;
      const attempt: Omit<Attempt, "module"> = {
        char: `dst-${card.solve}`,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "chartwork" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <p style={{ marginTop: 0, fontSize: 18, marginBottom: 12 }}>{card.prompt}</p>

        {!revealed && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {card.choices.map((c) => (
              <button
                key={c}
                className="secondary"
                onClick={() => onChoose(c)}
                style={{ fontSize: 18, padding: "14px 0", fontWeight: 600 }}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.correct
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.correct ? "✓ Correct." : `✗ It's ${card.correct}.`}
            </p>
            <p className="small muted" style={{ margin: "6px 0 0" }}>
              {card.formula}
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
